import * as React from "react";
import { Search, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Barra de ferramentas da página: período, visão e filtros à esquerda; busca e
 * exportação à direita.
 *
 * **Sem card, sem borda, sem fundo, sem o rótulo "Filtros:".** A toolbar é
 * transparente sobre o fundo da página — o card "Filtros" que emoldurava os
 * selects em Colheitas, Mangarás e Cachos a colher morreu com esta casca.
 *
 * Todo controle filho em `h-9`: é a altura já usada por `Input`,
 * `SelectTrigger` e `Button size="default"`. Não invente outra.
 *
 * **No celular a barra EMPILHA e os filtros QUEBRAM em linhas.** A versão
 * anterior era uma linha só: os filtros num `overflow-x-auto` e a busca
 * (`w-full` abaixo de `sm`) num irmão `shrink-0`. A busca comia a linha, a
 * faixa de filtros encolhia até caber um controle, e os outros três sumiam
 * num scroll horizontal sem nenhuma pista de que existiam — em Vendas o
 * celular mostrava só "Mês atual". Filtro que não se vê não se usa: nada de
 * esconder controle atrás de gesto invisível.
 */
export interface PageToolbarProps {
  /** Filtros — à esquerda. */
  children: React.ReactNode;
  /** Busca / exportar — à direita. */
  fim?: React.ReactNode;
  className?: string;
}

export function PageToolbar({ children, fim, className }: PageToolbarProps) {
  return (
    <div className={cn("flex flex-col gap-2 md:flex-row md:items-center md:justify-between", className)}>
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        {children}
      </div>
      {fim && <div className="flex flex-wrap items-center gap-2 md:shrink-0">{fim}</div>}
    </div>
  );
}

/**
 * O campo de busca da plataforma — o slot `fim` natural da toolbar.
 *
 * Havia ZERO campos de busca em 50 telas, algumas com centenas de linhas. Este
 * é filtro no cliente, sobre a lista já carregada (ver `lib/busca.ts`): nenhum
 * `queryKey` muda.
 *
 * Só entra onde a lista pode passar de ~15 linhas. Abaixo disso a busca é
 * ruído, e a página fica com um campo que ninguém usa ocupando a linha.
 */
export interface PageSearchProps {
  valor: string;
  onChange: (v: string) => void;
  placeholder: string;
  className?: string;
  testId?: string;
}

export function PageSearch({ valor, onChange, placeholder, className, testId }: PageSearchProps) {
  return (
    /* `md`, não `sm`: é em `md` que a toolbar deixa de empilhar. Em `sm` a busca
       encolhia para 180px sozinha no meio de uma linha vazia. */
    <div className={cn("relative w-full md:w-[180px]", className)}>
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
      <Input
        type="search"
        value={valor}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 pl-8 pr-8 text-sm"
        data-testid={testId ?? "input-busca"}
      />
      {valor && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Limpar busca"
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          data-testid="button-limpar-busca"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
